import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import candidateService from '../../../api/services/candidateService';
import {
    experienceService,
    skillService,
    sourceService,
    candidateAvailabilityService
} from '../../../api/crm/config/configService';
import { toast } from 'react-hot-toast';

const STATIC_FIELD_VALUES = {
    status: ['applied', 'screening', 'shortlisted', 'interview', 'interviewed', 'offer', 'offered', 'hired', 'rejected'],
    noticePeriod: ['immediate', '15-days', '30-days', '60-days', '90-days']
};

const BULK_UPLOAD_FIELD_INSTRUCTIONS = [
    {
        label: 'First Name',
        column: 'firstName',
        required: true,
        message: 'Enter the candidate first name. Minimum 2 characters recommended.'
    },
    {
        label: 'Last Name',
        column: 'lastName',
        required: true,
        message: 'Enter the candidate last name.'
    },
    {
        label: 'Email',
        column: 'email',
        required: true,
        message: 'Enter a valid candidate email address. Example: john.doe@example.com'
    },
    {
        label: 'Phone',
        column: 'phone',
        required: true,
        message: 'Enter the candidate phone number. Example: 9876543210'
    },
    {
        label: 'Job Position',
        column: 'jobTitle',
        required: true,
        message: 'Enter the job title exactly as configured in active job posts.',
        valuesKey: 'jobTitles'
    },
    {
        label: 'Department',
        column: 'department',
        required: true,
        message: 'Enter the department name exactly as configured in master data.',
        valuesKey: 'departments'
    },
    {
        label: 'Experience',
        column: 'experience',
        required: true,
        message: 'Enter the experience value exactly as configured in Configure Management.',
        valuesKey: 'experienceLevels'
    },
    {
        label: 'Recruitment Status',
        column: 'status',
        required: false,
        message: 'Enter the candidate recruitment status. If left empty, the system uses applied.',
        valuesKey: 'status'
    },
    {
        label: 'Add Skills',
        column: 'skills',
        required: false,
        message: 'Enter multiple skills in one cell separated by semicolon (;). Example: React;Node.js;MongoDB. You can also add custom values.',
        valuesKey: 'skills'
    },
    {
        label: 'Street Address',
        column: 'streetAddress',
        required: false,
        message: 'Enter the street address for the candidate.'
    },
    {
        label: 'Postal Code',
        column: 'postalCode',
        required: false,
        message: 'Enter the postal or ZIP code. Example: 560001'
    },
    {
        label: 'Country',
        column: 'country',
        required: false,
        message: 'Enter the country name. Example: India'
    },
    {
        label: 'State',
        column: 'state',
        required: false,
        message: 'Enter the state name. Example: Karnataka'
    },
    {
        label: 'City',
        column: 'city',
        required: false,
        message: 'Enter the city name. Example: Bangalore'
    },
    {
        label: 'Current Company',
        column: 'currentCompany',
        required: false,
        message: 'Enter the candidate current company name.'
    },
    {
        label: 'Expected Salary',
        column: 'expectedSalary',
        required: false,
        message: 'Enter the expected salary. Example: 15,00,000'
    },
    {
        label: 'Current Salary',
        column: 'currentSalary',
        required: false,
        message: 'Enter the current salary. Example: 12,00,000'
    },
    {
        label: 'Notice Period',
        column: 'noticePeriod',
        required: false,
        message: 'Enter the notice period using the exact stored value from Add Candidate.',
        valuesKey: 'noticePeriod'
    },
    {
        label: 'Education',
        column: 'education',
        required: true,
        message: 'Enter the highest education or degree. Example: B.Tech'
    },
    {
        label: 'University',
        column: 'university',
        required: true,
        message: 'Enter the university or institute name. Example: IIT Bombay'
    },
    {
        label: 'Graduation Year',
        column: 'graduationYear',
        required: false,
        message: 'Enter the graduation year as a 4-digit number. Example: 2018'
    },
    {
        label: 'LinkedIn',
        column: 'linkedin',
        required: false,
        message: 'Enter the LinkedIn profile URL. Example: https://linkedin.com/in/johndoe'
    },
    {
        label: 'GitHub',
        column: 'github',
        required: false,
        message: 'Enter the GitHub profile URL. Example: https://github.com/johndoe'
    },
    {
        label: 'Portfolio',
        column: 'portfolio',
        required: false,
        message: 'Enter the portfolio or website URL. Example: https://johndoe.me'
    },
    {
        label: 'Source',
        column: 'source',
        required: false,
        message: 'Enter the candidate source exactly as configured in master data.',
        valuesKey: 'sources'
    },
    {
        label: 'Notes',
        column: 'notes',
        required: false,
        message: 'Enter any additional notes about the candidate.'
    },
    {
        label: 'Availability',
        column: 'availability',
        required: false,
        message: 'Enter the candidate availability exactly as configured in master data.',
        valuesKey: 'availabilities'
    }
];

/**
 * CandidateBulkUploadModal - Modal for bulk uploading candidates from CSV
 */
const CandidateBulkUploadModal = ({ isOpen, onClose, onSuccess, jobs = [], departments = [] }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedCandidates, setParsedCandidates] = useState([]);
    const [validationResult, setValidationResult] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const fileInputRef = useRef(null);

    const jobTitles = useMemo(
        () => jobs.map((job) => job.label).filter(Boolean),
        [jobs]
    );

    const departmentNames = useMemo(
        () => departments.map((department) => department.label).filter(Boolean),
        [departments]
    );

    const { data: experienceLevels = [], isLoading: isLoadingExperience } = useQuery({
        queryKey: ['candidateBulkUploadExperienceLevels'],
        queryFn: async () => {
            const result = await experienceService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: skills = [], isLoading: isLoadingSkills } = useQuery({
        queryKey: ['candidateBulkUploadSkills'],
        queryFn: async () => {
            const result = await skillService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: sources = [], isLoading: isLoadingSources } = useQuery({
        queryKey: ['candidateBulkUploadSources'],
        queryFn: async () => {
            const result = await sourceService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: availabilities = [], isLoading: isLoadingAvailabilities } = useQuery({
        queryKey: ['candidateBulkUploadAvailabilities'],
        queryFn: async () => {
            const result = await candidateAvailabilityService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const fieldValueMap = useMemo(() => ({
        jobTitles,
        departments: departmentNames,
        experienceLevels,
        skills,
        sources,
        availabilities,
        ...STATIC_FIELD_VALUES
    }), [jobTitles, departmentNames, experienceLevels, skills, sources, availabilities]);

    const isLoadingFieldValues =
        isLoadingExperience ||
        isLoadingSkills ||
        isLoadingSources ||
        isLoadingAvailabilities;

    const getFieldValues = (valuesKey) => {
        if (!valuesKey) return [];
        return fieldValueMap[valuesKey] || [];
    };

    // Reset state when modal closes
    const handleClose = useCallback(() => {
        setStep('upload');
        setFile(null);
        setParsedCandidates([]);
        setValidationResult(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
        onClose();
    }, [onClose]);

    // Handle file selection
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validate file type
        const validTypes = ['text/csv', 'application/vnd.ms-excel', '.csv'];
        const isValidType = validTypes.some(type =>
            selectedFile.type === type || selectedFile.name.endsWith('.csv')
        );

        if (!isValidType) {
            toast.error('Please upload a CSV file');
            return;
        }

        // Validate file size (max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        // Read and parse the file
        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvContent = event.target?.result;
            if (typeof csvContent !== 'string') return;

            const result = candidateService.parseCSV(csvContent);
            if (!result.success) {
                toast.error(result.error || 'Failed to parse CSV');
                setFile(null);
                return;
            }

            if (result.candidates.length === 0) {
                toast.error('No valid candidate data found in CSV');
                setFile(null);
                return;
            }

            if (result.candidates.length > 500) {
                toast.error('Maximum 500 candidates per upload. Please split your file.');
                setFile(null);
                return;
            }

            setParsedCandidates(result.candidates);
            setStep('preview');

            // Auto-validate
            await validateCandidates(result.candidates);
        };

        reader.onerror = () => {
            toast.error('Failed to read file');
            setFile(null);
        };

        reader.readAsText(selectedFile);
    };

    // Validate parsed candidates
    const validateCandidates = async (candidates) => {
        setIsValidating(true);
        try {
            const result = await candidateService.validateBulkUpload(candidates);
            setValidationResult(result);
        } catch (error) {
            toast.error('Validation failed: ' + (error.message || 'Unknown error'));
        } finally {
            setIsValidating(false);
        }
    };

    // Download template
    const handleDownloadTemplate = async () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;

        const filename = `candidate_bulk_upload_template_${day}-${month}-${year}_${String(hours).padStart(2, '0')}-${minutes}_${ampm}.csv`;
        const result = await candidateService.downloadBulkUploadTemplate(filename, { jobs, departments });
        if (!result.success) {
            toast.error(result.error || 'Failed to download template');
        } else {
            toast.success('Template downloaded');
        }
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile) {
            const event = { target: { files: [droppedFile] } };
            handleFileChange(event);
        }
    };

    // Perform the actual upload
    const handleUpload = async () => {
        if (parsedCandidates.length === 0) return;

        setIsUploading(true);
        setStep('uploading');

        try {
            const result = await candidateService.bulkUploadCandidates(parsedCandidates, { skipDuplicates });
            setUploadResult(result);
            setStep('result');

            if (result.success) {
                const created = result.data?.created || 0;
                const skipped = result.data?.skipped || 0;
                const failed = result.data?.failed || 0;
                const parts = [];
                if (created > 0) parts.push(`${created} created`);
                if (skipped > 0) parts.push(`${skipped} skipped`);
                if (failed > 0) parts.push(`${failed} failed`);
                toast.success(`Successfully ${parts.join(', ')}`);
                if (onSuccess) onSuccess();
            } else {
                toast.error(result.error || result.message || 'No candidates were created');
            }
        } catch (error) {
            toast.error(error.message || 'Upload failed');
            setUploadResult({
                success: false,
                error: error.message || 'Upload failed'
            });
            setStep('result');
        } finally {
            setIsUploading(false);
        }
    };

    // Go back to previous step
    const handleBack = () => {
        if (step === 'preview') {
            setStep('upload');
            setFile(null);
            setParsedCandidates([]);
            setValidationResult(null);
        } else if (step === 'result') {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-agrosaf-green/10 rounded-lg">
                            <Upload className="h-5 w-5 text-agrosaf-green" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Candidates</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload a CSV file with candidate data'}
                                {step === 'preview' && (() => {
                                    const errorCount = validationResult?.data?.failed || 0;
                                    const duplicateCount = validationResult?.data?.duplicates?.length || 0;
                                    const readyCount = Math.max(0, parsedCandidates.length - errorCount - duplicateCount);
                                    if (readyCount > 0) {
                                        return `${readyCount} of ${parsedCandidates.length} candidates ready for upload`;
                                    } else {
                                        return `${parsedCandidates.length} candidates parsed, none ready for upload`;
                                    }
                                })()}
                                {step === 'uploading' && 'Uploading candidates...'}
                                {step === 'result' && 'Upload complete'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Upload Step */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            {/* Drag & Drop Zone */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-agrosaf-green hover:bg-agrosaf-green/5 transition-all cursor-pointer"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700 mb-2">
                                    Drag & drop your CSV file here
                                </p>
                                <p className="text-sm text-gray-500 mb-4">
                                    or click to browse (Max 500 candidates, 5MB)
                                </p>
                                <Button variant="outline" icon={<Upload className="h-4 w-4" />}>
                                    Select File
                                </Button>
                            </div>

                            {/* Template Download */}
                            <div className="bg-blue-50 rounded-xl p-4 flex items-start space-x-4">
                                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                    <FileText className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-900 mb-1">
                                        Need a template?
                                    </h4>
                                    <p className="text-sm text-blue-700 mb-3">
                                        Download our CSV template with all the required fields and sample data.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Download className="h-4 w-4" />}
                                        onClick={handleDownloadTemplate}
                                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                    >
                                        Download Template
                                    </Button>
                                </div>
                            </div>

                            {/* Field Instructions */}
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">CSV Field Instructions</h4>
                                <p className="text-xs text-gray-500 mb-4">
                                    Use the same column names as the downloaded template. Enter values exactly as mentioned below.
                                </p>

                                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                                    {BULK_UPLOAD_FIELD_INSTRUCTIONS.map((field) => {
                                        const values = getFieldValues(field.valuesKey);
                                        const showValues = Boolean(field.valuesKey);

                                        return (
                                            <div key={field.column} className="bg-white border border-gray-200 rounded-lg p-4">
                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                    <h5 className="text-sm font-semibold text-gray-900">{field.label}</h5>
                                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${field.required ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                        {field.required ? 'Mandatory' : 'Optional'}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500 font-mono">CSV column: {field.column}</span>
                                                </div>

                                                <p className="text-sm text-gray-700 mb-3">{field.message}</p>

                                                {showValues && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                                            Accepted Values
                                                        </p>
                                                        {isLoadingFieldValues ? (
                                                            <div className="flex items-center text-xs text-gray-500">
                                                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                                                                Loading accepted values...
                                                            </div>
                                                        ) : values.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {values.map((value) => (
                                                                    <code
                                                                        key={`${field.column}-${value}`}
                                                                        className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-xs font-mono text-gray-800"
                                                                    >
                                                                        {value}
                                                                    </code>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-500">No configured values found.</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview Step */}
                    {step === 'preview' && (
                        <div className="space-y-6">
                            {/* File Info */}
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <FileSpreadsheet className="h-8 w-8 text-agrosaf-green" />
                                    <div>
                                        <p className="font-medium text-gray-900">{file?.name}</p>
                                        <p className="text-sm text-gray-500">{parsedCandidates.length} candidates parsed</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setFile(null);
                                        setParsedCandidates([]);
                                        setValidationResult(null);
                                        setStep('upload');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Validation Status */}
                            {isValidating ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-agrosaf-green" />
                                    <span className="ml-3 text-gray-600">Validating candidates...</span>
                                </div>
                            ) : validationResult && (() => {
                                const totalItems = parsedCandidates.length;
                                const errorCount = validationResult.data?.failed || 0;
                                const duplicateCount = validationResult.data?.duplicates?.length || 0;
                                const readyToUpload = Math.max(0, totalItems - errorCount - duplicateCount);

                                return (
                                    <div className="space-y-4">
                                        {/* Summary */}
                                        <div className="grid grid-cols-4 gap-3">
                                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                                <p className="text-xl font-bold text-blue-700">
                                                    {totalItems}
                                                </p>
                                                <p className="text-xs text-blue-600">Total Parsed</p>
                                            </div>
                                            <div className="bg-red-50 rounded-lg p-3 text-center">
                                                <AlertCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-red-700">
                                                    {errorCount}
                                                </p>
                                                <p className="text-xs text-red-600">Errors</p>
                                            </div>
                                            <div className="bg-yellow-50 rounded-lg p-3 text-center">
                                                <AlertTriangle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-yellow-700">
                                                    {duplicateCount}
                                                </p>
                                                <p className="text-xs text-yellow-600">Duplicates</p>
                                            </div>
                                            <div className={`rounded-lg p-3 text-center ${readyToUpload > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                                                <CheckCircle className={`h-5 w-5 mx-auto mb-1 ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                                                <p className={`text-xl font-bold ${readyToUpload > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                                                    {readyToUpload}
                                                </p>
                                                <p className={`text-xs ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-500'}`}>Ready to Upload</p>
                                            </div>
                                        </div>

                                        {/* Errors */}
                                        {validationResult.data?.errors?.length > 0 && (
                                            <div className="bg-red-50 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-sm font-medium text-red-800">Validation Errors</h4>
                                                    <button
                                                        onClick={() => {
                                                            const allErrors = validationResult.data.errors.join('\n');
                                                            navigator.clipboard.writeText(allErrors);
                                                            toast.success(`Copied ${validationResult.data.errors.length} errors to clipboard`);
                                                        }}
                                                        className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-2 py-1 rounded transition-colors"
                                                        title="Copy all errors"
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                        <span>Copy All</span>
                                                    </button>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto space-y-2">
                                                    {validationResult.data.errors.slice(0, 10).map((error, idx) => (
                                                        <div key={idx} className="text-sm text-red-700 bg-red-100/50 p-2 rounded border border-red-200">
                                                            <span className="font-bold mr-1">Row {error.row}:</span>
                                                            {Array.isArray(error.errors) ? error.errors.join(', ') : (error.error || String(error))}
                                                        </div>
                                                    ))}
                                                    {validationResult.data.errors.length > 10 && (
                                                        <p className="text-sm text-red-600 font-medium px-2">
                                                            ... and {validationResult.data.errors.length - 10} more errors
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Skip Duplicates Option */}
                                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                                            <input
                                                type="checkbox"
                                                id="skipDuplicates"
                                                checked={skipDuplicates}
                                                onChange={(e) => setSkipDuplicates(e.target.checked)}
                                                className="h-4 w-4 text-agrosaf-green rounded border-gray-300 focus:ring-agrosaf-green"
                                            />
                                            <label htmlFor="skipDuplicates" className="text-sm text-gray-700">
                                                Skip duplicate candidates and continue with upload
                                            </label>
                                        </div>

                                        {/* Preview Table */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 px-4 py-2 border-b">
                                                <h4 className="text-sm font-medium text-gray-900">Preview (First 5 candidates)</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">First Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {parsedCandidates.slice(0, 5).map((candidate, idx) => (
                                                            <tr key={idx}>
                                                                <td className="px-4 py-2 text-sm text-gray-900 font-medium">{candidate.firstName}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{candidate.lastName}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{candidate.email}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{candidate.jobTitle}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Uploading Step */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="h-12 w-12 animate-spin text-agrosaf-green mb-4" />
                            <p className="text-lg font-medium text-gray-900 mb-2">Uploading Candidates...</p>
                            <p className="text-sm text-gray-500">Please wait while we process your data</p>
                        </div>
                    )}

                    {/* Result Step */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`text-center py-8 rounded-xl ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                {uploadResult.success ? (
                                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                ) : (
                                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                                )}
                                <h3 className={`text-xl font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                    {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                                </h3>
                                <p className={`text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {uploadResult.message || uploadResult.error}
                                </p>
                            </div>

                            {uploadResult.data && (
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-700">{uploadResult.data.total || 0}</p>
                                        <p className="text-sm text-gray-500">Total</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-green-700">{uploadResult.data.created || 0}</p>
                                        <p className="text-sm text-green-600">Created</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-red-700">{uploadResult.data.failed || 0}</p>
                                        <p className="text-sm text-red-600">Failed</p>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-yellow-700">{uploadResult.data.skipped || 0}</p>
                                        <p className="text-sm text-yellow-600">Skipped</p>
                                    </div>
                                </div>
                            )}

                            {uploadResult.data?.errors?.length > 0 && (
                                <div className="max-h-48 overflow-y-auto bg-red-50 rounded-lg p-4 border border-red-100">
                                    <h4 className="text-sm font-semibold text-red-800 mb-2">Upload Errors</h4>
                                    <ul className="space-y-2">
                                        {uploadResult.data.errors.map((err, idx) => (
                                            <li key={idx} className="text-sm text-red-700 bg-white/50 p-2 rounded border border-red-200">
                                                <span className="font-bold mr-1">Row {err.row}:</span>
                                                {Array.isArray(err.errors) ? err.errors.join(', ') : (err.error || String(err))}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-3">
                        {step === 'preview' && (
                            <Button variant="outline" onClick={handleBack}>
                                Back
                            </Button>
                        )}
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                    </div>

                    {step === 'preview' && (() => {
                        const errorCount = validationResult?.data?.failed || 0;
                        const validJobsCount = Math.max(0, parsedCandidates.length - errorCount - (skipDuplicates ? (validationResult?.data?.duplicates?.length || 0) : 0));
                        const hasNoValidJobs = validJobsCount <= 0;

                        return (
                            <div className="flex items-center space-x-3">
                                {hasNoValidJobs && (
                                    <span className="text-sm text-red-600">No valid candidates to upload</span>
                                )}
                                <Button
                                    variant="primary"
                                    icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    onClick={handleUpload}
                                    disabled={isUploading || isValidating || hasNoValidJobs}
                                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen disabled:bg-gray-400"
                                >
                                    {isUploading ? 'Uploading...' : `Upload ${validJobsCount} Candidates`}
                                </Button>
                            </div>
                        );
                    })()}

                    {step === 'result' && (
                        <Button
                            variant="primary"
                            onClick={handleClose}
                            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        >
                            Done
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CandidateBulkUploadModal;
